import {
    BackgroundCheckError,
    BackgroundCheckHistoryColumns,
    BackgroundCheckHistoryData,
    backgroundCheckHistoryEntityName,
} from '@/backend/features/background-check';
import { BackgroundCheckClient } from '@/backend/features/background-check/client/backgroundCheckClient';
import { JnetRequestData, JnetResponse } from '@/backend/features/background-check/client/types';
import { BackgroundCheckConfig, BackgroundCheckQuota, BackgroundCheckTest } from '@/backend/features/background-check/defaults';
import { validateJnetRequest } from '@/backend/features/background-check/platform-functions/utils/validateJnetRequest';
import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { BackgroundCheckStatus } from '@/backend/features/background-check/types';
import { ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { AppContext } from '@juvare/platform-app/App';
import { AppState } from '@juvare/platform-sdk-react/types';

export interface RequestJnetBackgroundCheckParams {
    context: AppContext<AppState>;
    config: BackgroundCheckConfig;
    accountData: ExtendedAccountData;
    quota: BackgroundCheckQuota;
    backgroundCheckTestConfig: BackgroundCheckTest;
    accountId: string;
    backgroundCheck: BackgroundCheckColumns;
    isTestMode: boolean;
    requestedBy: string;
}

export const requestJnetBackgroundCheck = ({
    context,
    config,
    accountData,
    quota,
    backgroundCheckTestConfig,
    accountId,
    backgroundCheck,
    isTestMode,
    requestedBy,
}: RequestJnetBackgroundCheckParams) => {
    const validateResponse = validateJnetRequest({
        context,
        accountData,
        backgroundCheck,
        config,
        quota,
    });

    const jnetErrorMessage = 'Error while processing JNET request.';

    const request: JnetRequestData = {
        username: config.client,
        firstName: validateResponse.person.firstName,
        lastName: validateResponse.person.lastName,
        birthDate: validateResponse.person.dob,
        ssn: validateResponse.person.ssn.slice(-4), // last numbers of SSN are needed
    };

    if (backgroundCheckTestConfig.useTestData && Number.isInteger(backgroundCheckTestConfig.jnetReturnCode)) {
        request.test = {
            returnCode: backgroundCheckTestConfig.jnetReturnCode,
        };
    }

    const backgroundCheckClient = new BackgroundCheckClient({
        encryption: context.encryption,
        jasBaseURL: context.screening.baseUrl,
        httpClient: context.httpClient,
        awsConfig: {
            awsAccessKey: context.appConfig.platformJasAccessKey,
            awsSecretKey: context.appConfig.platformJasSecretAccessKey,
            awsRegion: context.appConfig.platformJasRegion,
        },
    });

    try {
        const response = backgroundCheckClient.requestJNet(request);

        // If a JNET request fails (result = false), check the response in JAS for an error code.
        // Error code definitions are documented in the file attached at the end of the Confluence page:
        // https://confluence.juvare.com/spaces/SE/pages/5197904/PA+JNET+PA+MAN
        if (!response.result) {
            createJnetHistory({
                context,
                accountId,
                backgroundCheckStatus: backgroundCheck.status as BackgroundCheckStatus,
                nextStatus: 'processingError',
                message: jnetErrorMessage,
                request,
                response,
                isTestMode,
                requestedBy: requestedBy,
            });

            throw new Error(jnetErrorMessage);
        }

        return createJnetHistory({
            context,
            accountId,
            request,
            backgroundCheckStatus: backgroundCheck.status as BackgroundCheckStatus,
            nextStatus: 'completeClear',
            response,
            isUsingQuota: validateResponse.isUsingQuota,
            isManualChange: true,
            isTestMode,
            requestedBy: requestedBy,
        });
    } catch (e) {
        if (e instanceof BackgroundCheckError) {
            try {
                createJnetHistory({
                    context,
                    accountId,
                    backgroundCheckStatus: backgroundCheck.status as BackgroundCheckStatus,
                    nextStatus: e.backgroundCheckStatus,
                    message: e.message,
                    isTestMode,
                    request,
                    requestedBy: requestedBy,
                });
            } catch (e) {
                console.error(e);
            }

            throw e;
        } else {
            if (e instanceof Error && e.message !== jnetErrorMessage) {
                try {
                    createJnetHistory({
                        context,
                        accountId,
                        backgroundCheckStatus: backgroundCheck.status as BackgroundCheckStatus,
                        nextStatus: 'processingError',
                        message: handleJNetRequestError(e),
                        isTestMode,
                        requestedBy: requestedBy,
                    });
                } catch (e) {
                    console.error(e);
                }
            }

            throw e;
        }
    }
};

interface CreateJnetHistoryParams {
    context: AppContext<AppState>;
    accountId: string;
    backgroundCheckStatus: BackgroundCheckStatus;
    nextStatus: BackgroundCheckStatus;
    response?: JnetResponse;
    request?: JnetRequestData;
    message?: string;
    isUsingQuota?: boolean;
    isManualChange?: boolean;
    isTestMode: boolean;
    requestedBy: string;
}

function createJnetHistory({
    requestedBy,
    context,
    accountId,
    backgroundCheckStatus,
    nextStatus,
    response,
    request,
    message,
    isUsingQuota,
    isManualChange,
    isTestMode,
}: CreateJnetHistoryParams) {
    const storage = context.storage<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns>(backgroundCheckHistoryEntityName);
    storage.post({
        action: 'create',
        data: {
            accountId,
            type: 'jnet',
            prevStatus: backgroundCheckStatus ?? undefined,
            requestedBy: requestedBy,
            nextStatus,
            request,
            requestId: response?.requestId,
            checkResponseByRequestId: response?.requestId,
            message,
            isUsingQuota,
            isManualChange,
            isTestMode,
        },
    });
}

function handleJNetRequestError(e: unknown): string {
    if (e instanceof Error) {
        try {
            const parsed = JSON.parse(e.message);
            if (parsed?.error === 'PAJNETError: wrong request format') {
                return 'Wrong request format';
            }
            if (parsed?.error === 'PAJNETError: bad accessToken') {
                return 'Bad access token';
            }
        } catch {
            // not JSON, ignore
        }
        return e.message || 'Unknown error';
    }
    return 'Unknown error';
}
